import { FC, useCallback, useRef } from "react";
import { useRouter } from "next/router";
import dynamic from "next/dynamic";
import styled from "@emotion/styled";
import { Drawer } from "src/atoms/Drawer";
import { Sticky } from "src/atoms/Sticky";
import {
  useIsOpenNavigationMenu,
  useIsOpenSearch,
} from "src/hooks/stores/use-header-store";
import { headerStore } from "src/stores/header/header.store";
import { Navigation } from "./Navigation";
import { NavMenu } from "./NavMenu";
import { SubMenu } from "./SubMenu";
import { ServiceBanner } from "./ServiceBanner";
import { locale } from "src/config/app-config";
import { useRibbon } from "src/hooks/use-ribbon";
import { AddedToCart } from "src/molecules/AddedtoCart";
import { GuestOrderLookupSheet } from "src/molecules/GuestOrderLookup";
import { Heading } from "@otrium/atoms";
import { AlertMessage } from "src/molecules/AlertMessage";
import { useShopTypeContext } from "src/hooks/context/use-shop-type-context";
import { ShopType } from "src/utils/genderLocale/genderLocale";
import { SEARCH_ANIMATION_TIME } from "src/utils/constants";
import { isConfirmationStep } from "src/utils/checkout";

const Ribbon = dynamic(
  () => import("src/molecules/Ribbon").then((mod) => mod.Ribbon),
  { ssr: true }
);

const StyledHeader = styled.header<{
  isOpenSearch: boolean;
  simplifiedHeader: boolean;
  duration?: number;
}>`
  border-bottom-style: solid;
  border-bottom-color: ${({ isOpenSearch, theme }) =>
    isOpenSearch ? "transparent" : theme.colors.primary.platinumLight};
  display: block !important;

  @media (min-width: ${({ theme }) => theme.breakpoints[2]}) {
    border-bottom-width: 1px;
    transition: border-bottom-color ${({ duration }) => duration || 0}ms
      ease-out;
  }

  @media (max-width: ${({ theme }) => theme.breakpoints[2]}) {
    border-bottom-width: ${({ simplifiedHeader }) =>
      simplifiedHeader ? "1px" : "0"};
  }

  .Header__searchModal {
    position: absolute;
    top: 84px;
    width: 100%;

    @media (max-width: ${({ theme }) => theme.breakpoints[2]}) {
      position: static;
    }
  }

  .Header_Navigation {
    position: relative;
    z-index: 1102;
  }

  .Drawer__closeContainer.Drawer__closeMenuBtn {
    top: 4px;

    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

interface Props {
  isLoginFlow: boolean;
  isCheckoutPage?: boolean;
}

const hideSubMenuRoutes = [
  "/not-found",
  "/my-profile",
  "/my-orders",
  "/my-o-credit",
  "/returns",
];

const Header: FC<Props> = ({ isLoginFlow, isCheckoutPage }) => {
  const router = useRouter();
  const gendersRef = useRef<HTMLDivElement>(null);
  // Anchors the header's "has the page scrolled" checks: the compact search
  // affordances appear once this banner has gone.
  const serviceBannerRef = useRef<HTMLDivElement>(null);
  const { currentShopType } = useShopTypeContext();

  const isOpenNavigationMenu = useIsOpenNavigationMenu();

  const handleCloseNavigationMenu = useCallback(
    () => headerStore.closeNavigationMenu(),
    []
  );
  const { showRibbon, toggleShowRibbon, discountMessage, combinedAlertBars } =
    useRibbon();
  const isRibbonVisible =
    showRibbon && !isLoginFlow && currentShopType !== ShopType.All;
  const simplifiedHeader =
    (isCheckoutPage || false) && !isConfirmationStep(router);

  const isOpenSearch = useIsOpenSearch();

  const isCartPage = router.route === "/cart";
  const isCartOrCheckoutPage = isCartPage || simplifiedHeader;

  return (
    <StyledHeader
      isOpenSearch={isOpenSearch}
      duration={SEARCH_ANIMATION_TIME}
      simplifiedHeader={simplifiedHeader}
    >
      <Heading className="sr-only">Otrium</Heading>
      {(!isCartOrCheckoutPage || !discountMessage) && (
        <>
          {!simplifiedHeader && (
            <ServiceBanner
              ref={serviceBannerRef}
              aria-hidden={isRibbonVisible}
            />
          )}
          {isRibbonVisible && combinedAlertBars.length > 0 && (
            <Ribbon
              data-testid="ribbon"
              alertBars={combinedAlertBars}
              onClick={toggleShowRibbon}
            />
          )}
        </>
      )}
      {isLoginFlow ? (
        <Navigation
          className="Header_Navigation"
          gendersRef={gendersRef}
          serviceBannerRef={serviceBannerRef}
          simplifiedHeader={simplifiedHeader}
          isLoginFlow={isLoginFlow}
        />
      ) : (
        <Sticky isHeader hideShadowOnMobile={isCartPage}>
          {isCartOrCheckoutPage && discountMessage && (
            <AlertMessage isVisible message={discountMessage} />
          )}
          <Navigation
            className="Header_Navigation"
            gendersRef={gendersRef}
            serviceBannerRef={serviceBannerRef}
            simplifiedHeader={simplifiedHeader}
          />
          <AddedToCart />
        </Sticky>
      )}

      {!hideSubMenuRoutes.includes(router.route) &&
      !isLoginFlow &&
      !simplifiedHeader ? (
        <SubMenu
          genderId={currentShopType}
          locale={locale}
          gendersRef={gendersRef}
          serviceBannerRef={serviceBannerRef}
          searchAnimationTime={SEARCH_ANIMATION_TIME}
        />
      ) : null}

      <Drawer isOpen={isOpenNavigationMenu} onClose={handleCloseNavigationMenu}>
        {isOpenNavigationMenu && (
          <NavMenu
            locale={locale}
            onClose={handleCloseNavigationMenu}
            isLoginFlow={isLoginFlow}
          />
        )}
      </Drawer>

      <GuestOrderLookupSheet />
    </StyledHeader>
  );
};

export { Header };
